<template>
  <div class="products">
    <v-row dense wrap align="center" class="productsIntro">
      <v-col cols="12" md="6">
        <h1 class="display-1 pageTitle">{{ $t("PRODUCTS_HEADER") }}</h1>
        <div :class="['productsDescription', {expanded: descriptionExpanded}]">
          <div id="productsDescriptionText" class="subheading" v-html="$t('PRODUCTS_TEXT')"></div>
          <span v-if="!descriptionExpanded" class="descriptionEllipsis" aria-hidden="true">...</span>
          <button v-if="!descriptionExpanded" type="button" class="descriptionToggle"
                  aria-expanded="false"
                  aria-controls="productsDescriptionText"
                  @click="descriptionExpanded = true">
            {{ $t('PRODUCTS_READ_MORE') }}
          </button>
        </div>
      </v-col>
      <v-col cols="12" md="6" class="productsDiscount" :class="{'d-none d-md-block': !hasCoupons}">
        <ShopNavigationActions :step="3" class="desktopNavigation d-none d-md-flex"/>
        <StickyDiscountBanner :get-boundary="getBoundary" v-if="hasCoupons" :suppress-floating="configuring"/>
      </v-col>
      <v-col v-if="false" cols="12" class="whiteCard christmas">
        <v-row wrap row>
          <v-col class="iconFlex">
            <img :src="christmasImage" class="christmasImage"/>
          </v-col>
          <v-col class="textFlex">
            <p class="text"><b>Tipp: </b>Weihnachten steht vor der Tür! Profitieren Sie von unserer großen Auswahl an <b>Geschenkprodukten</b> weiter unten auf der Seite, um Ihren Liebsten eine Freude zu machen.</p>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <!-- Product Category Tabs -->
    <v-row wrap v-if="availableCategories.length > 0" class="category-row">
      <v-col cols="12">
        <div class="category-tabs" role="group" :aria-label="$t('PRODUCTS_HEADER')">
          <v-btn depressed :class="['category-chip', {'v-tab--active': selectedCategoryIndex === 0}]"
                 :aria-pressed="selectedCategoryIndex === 0 ? 'true' : 'false'"
                 @click="selectedCategoryIndex = 0">
            <v-icon v-if="false" left small>mdi-view-grid</v-icon>
            {{ $t('PRODUCTS_ALL_CATEGORIES') }}
          </v-btn>
          <v-btn v-for="(category, index) in availableCategories" :key="category.id" depressed
                 :class="['category-chip', {'highlight-chip': category.highlight, 'v-tab--active': selectedCategoryIndex === index + 1}]"
                 :aria-pressed="selectedCategoryIndex === index + 1 ? 'true' : 'false'"
                 @click="selectedCategoryIndex = index + 1">
            <v-icon v-if="category.iconName" left small>{{ category.iconName }}</v-icon>
            {{ category.name }}
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-row wrap class="layout product-row" justify="center">
      <v-col v-for="product in filteredProducts" :key="product.id" class="product-col">
        <ProductDiscountBadge class="d-none d-md-inline-block" :price="product.price" :instead-of="product.setPriceInsteadOf"/>
        <v-card :class="{productCard: true, highlight: product.highlight}"
                @click.native="openProductOnMobile($event, product)">
          <span v-if="product.highlight" class="productHighlight" aria-hidden="true"></span>
          <router-link :to="`/product/${product.id}`" class="productImage" :aria-label="product.name">
            <v-img
              :src="product.imageUrl"
              class="productImageContent"
              contain
              lazy-src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAN4AAADjCAMAAADdXVr2AAAAA1BMVEX///+nxBvIAAAASElEQVR4nO3BMQEAAADCoPVPbQdvoAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAOA8W9AAHiq1isAAAAAElFTkSuQmCC"></v-img>
          </router-link>
          <div class="productInfo">
            <v-card-title class="productHeader">
              <ProductDiscountBadge class="d-md-none" :price="product.price" :instead-of="product.setPriceInsteadOf"/>
              <router-link :to="`/product/${product.id}`" class="productTitleLink">{{ product.name }}</router-link>
            </v-card-title>
            <div class="productBody">
              <div v-if="product.descriptionDisplay === descriptionModes.BULLETS" class="productContents">
                <ul v-if="product.highlightedPoints && product.highlightedPoints.length"
                    class="bulletPoints highlightedPoints" role="list">
                  <li v-for="(point, index) in product.highlightedPoints" :key="index">
                    <v-icon class="bulletIcon primary white--text" aria-hidden="true">mdi-check</v-icon>
                    <span>{{ point }}</span>
                  </li>
                </ul>
                <ul v-if="product.summaryPoints && product.summaryPoints.length"
                    class="bulletPoints summaryPoints grey--text text--darken-2" role="list">
                  <li v-for="(point, index) in product.summaryPoints" :key="index">
                    <v-icon class="bulletIcon" color="grey darken-2" aria-hidden="true">mdi-check</v-icon>
                    <span>{{ point }}</span>
                  </li>
                </ul>
              </div>
              <div v-else-if="product.shortDescription" class="productContents" v-html="product.shortDescription"></div>

              <ProductActions :price="product.price" :instead-of="product.setPriceInsteadOf"
                              :from="product.detailedConfigurationSurcharge > 0"
                              :label="$t('BUTTON_ADD_PRODUCT')" icon="add"
                              @configure="startConfiguration(product)"/>
            </div>
          </div>

        </v-card>
      </v-col>
    </v-row>

    <ProductConfigurationDialog ref="configurationDialog" v-if="configurationProduct" v-model="configuring" :product="configurationProduct"
                                @addedToBasket="onAddedToBasket" @error="onConfigurationError"/>
    <v-snackbar :value="!!configurationError" @input="configurationError = null" color="error" :timeout="5000">
      {{ configurationError }}
    </v-snackbar>

    <!-- Show All Products Button -->
    <v-row v-if="selectedCategory !== null" justify="center" class="show-all-row">
      <v-col cols="auto">
        <v-btn large outlined color="primary" @click="showAllProducts">
          {{ $t('PRODUCTS_SHOW_ALL') }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import Component from "vue-class-component";
import {ProductDto} from '@swagger';
import ProductConfigurationDialog from '@/components/product-configuration/ProductConfigurationDialog.vue';
import BasicComponent from "@/global/BasicComponent";
import {elementFromRef} from "@/global/ShopLayout";
import StickyDiscountBanner from "@/components/StickyDiscountBanner.vue";
import ProductActions from "@/components/ProductActions.vue";
import ProductDiscountBadge from "@/components/ProductDiscountBadge.vue";
import ShopNavigationActions from "@/components/ShopNavigationActions.vue";
import christmasImage from "@/assets/christmas.svg";
import {Watch} from "vue-property-decorator";

@Component({
  components: {ProductConfigurationDialog, ShopNavigationActions, StickyDiscountBanner, ProductActions, ProductDiscountBadge}
})
export default class Products extends BasicComponent {

  descriptionModes = ProductDto.DescriptionDisplayEnum;

  configurationProduct: ProductDto | null = null;
  configuring: boolean = false;
  configurationError: string | null = null;

  openProductOnMobile(event: MouseEvent, product: ProductDto) {
    if (!this.mobile || event.defaultPrevented) {
      return;
    }
    if (event.target instanceof Element && event.target.closest('a, button')) {
      return;
    }
    this.$router.push(`/product/${product.id}`);
  }

  startConfiguration(product: ProductDto) {
    this.configurationError = null;
    if (product.imageRequirements.length === 0) {
      this.addUnconfiguredProduct(product, item => {
        if (!item) return;
        this.configurationProduct = product;
        this.configuring = true;
        this.$nextTick(() => (this.$refs.configurationDialog as ProductConfigurationDialog).onAddedToBasket([item]));
      }, error => this.onConfigurationError(error));
      return;
    }
    this.configurationProduct = product;
    this.configuring = true;
  }

  onAddedToBasket() {
    this.configurationError = null;
  }

  onConfigurationError(error: string) {
    this.configurationError = error;
  }

  christmasImage = christmasImage;
  selectedCategoryIndex: number = 0;
  shouldScrollToTop: boolean = false;
  isLoadingFromQuery: boolean = false;
  descriptionExpanded: boolean = false;

  getBoundary(): HTMLElement | null {
    return elementFromRef(this.$el);
  }

  created() {
    this.requestProductCategories();
    this.loadCategoryFromQuery();
  }

  loadCategoryFromQuery() {
    this.isLoadingFromQuery = true;
    const catId = this.$route.query.cat;
    if (catId) {
      // Find the index of the category in availableCategories
      const categoryIndex = this.availableCategories.findIndex(cat => cat.id === Number(catId));
      if (categoryIndex !== -1) {
        this.selectedCategoryIndex = categoryIndex + 1; // +1 because index 0 is "All Categories"
      }
    } else {
      this.selectedCategoryIndex = 0;
    }
    this.$nextTick(() => {
      this.isLoadingFromQuery = false;
    });
  }

  @Watch('selectedCategoryIndex')
  onCategoryChange(newIndex: number, oldIndex: number) {
    // Don't update URL if this change came from loadCategoryFromQuery
    if (this.isLoadingFromQuery) {
      return;
    }

    // Build new URL with updated query parameter
    const url = new URL(window.location.href);

    if (newIndex === 0) {
      // "All Products" selected - remove query parameter
      url.searchParams.delete('cat');
    } else {
      // Specific category selected - set query parameter
      const category = this.availableCategories[newIndex - 1];
      if (category) {
        url.searchParams.set('cat', String(category.id));
      }
    }

    // Update URL without triggering navigation (and thus no scroll)
    window.history.replaceState({}, '', url.toString());

    // Only scroll to top if explicitly requested (via "Show All" button)
    if (this.shouldScrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.shouldScrollToTop = false;
    }
  }

  @Watch('$route.query.cat')
  onQueryChange() {
    this.loadCategoryFromQuery();
  }

  get selectedCategory(): number | null {
    if (this.selectedCategoryIndex === 0) {
      return null;
    }
    return this.availableCategories[this.selectedCategoryIndex - 1]?.id || null;
  }

  set selectedCategory(index: number) {
    this.selectedCategoryIndex = index;
  }

  get products() {
    return this.$store.getters.products;
  }

  get productCategories() {
    return this.$store.getters.productCategories;
  }

  get availableCategories() {
    // Only show categories that have products
    return this.productCategories.filter(category =>
      this.products.some(product => product.categoryIds && product.categoryIds.includes(category.id))
    );
  }

  get filteredProducts() {
    if (this.selectedCategory === null) {
      return this.products;
    }
    return this.products.filter(product =>
      product.categoryIds && product.categoryIds.includes(this.selectedCategory)
    );
  }

  getImageUrl(image) {
    var images = require.context('@/assets/', false, /\.jpg$/);
    return images('./' + image);
  }


  showAllProducts() {
    this.shouldScrollToTop = true;
    this.selectedCategoryIndex = 0;
  }

}
</script>
<style scoped lang="scss">
@use "../stylesheets/Variables.scss" as *;

.pageTitle {
  margin-bottom: 12px;
}
.pageTitle {

  margin-bottom: 12px;

}
.desktopNavigation {

  margin-bottom: 16px;

}

$highlight-border-width: 5px;
$mobile-card-padding: 12px;
$highlight-shimmer-duration: 4s;

.productsIntro {
  margin-bottom: 12px;
  .subheading {
    margin: 0;
  }
  .productsDiscount {
    padding-left: 20px;
  }
  @media (max-width: 959px) {
    .productsDiscount {
      padding: 8px 4px 0;
    }
    .productsDiscount ::v-deep .discountBanner {
      width: 100%;
    }
  }
}

.descriptionToggle, .descriptionEllipsis {

  display: none;

}
@media (max-width: 959px) {
  .pageTitle {
    margin-bottom: 6px;
  }
  .productsIntro {
    margin-bottom: 4px;
  }
  .category-row {
    margin-block: 8px 0;
    > .col {
      padding-block: 0;
    }
  }

  .category-row .category-tabs {

    margin-bottom: 4px;

  }
  .products > .product-row {
    margin-block: 0;
    .product-col {
      padding-block: 4px;
    }
  }

  .productsDescription {
    display: flex;
    align-items: baseline;
    gap: 8px;

    .subheading {
      flex: 1;
      min-width: 0;
      display: block;
      line-height: 1.5;
      max-height: 1.5em;
      white-space: nowrap;
      overflow: hidden;
    }

    &.expanded {
      display: block;
      .subheading {
        max-height: none;
        white-space: normal;
        overflow: visible;
      }
    }
  }

  .descriptionEllipsis {

    display: inline;

    flex-shrink: 0;

  }
  .descriptionToggle {
    display: inline-block;
    flex-shrink: 0;
    padding: 0;
    color: inherit;
    font: inherit;
    font-weight: 600;
    text-decoration: underline;

    &:focus-visible {

      outline: 2px solid var(--v-accent-base);

      outline-offset: 3px;

    }
  }
}

.v-alert.paymentPromotion {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-top: none;
  border-radius: 5px;
  background-color: $shop-white !important;
  color: $shop-black;
}

.topButtonRow {
  height: 100%;
  justify-content: flex-end;

  .flex {
    flex: 0 1 auto;
    align-self: end;
  }
}

.product-row {
  margin-bottom: 5px;
  align-items: stretch;

  .product-col {
    flex: 0 0 33.333333%;
    max-width: 33.333333%;
    min-width: 0;
    display: flex;
    position: relative;

    .discountBadge {
      position: absolute;
      top: -10px;
      right: -10px;
      z-index: 4;
    }

    .productCard {
      width: 100%;
      min-width: 0;
      display: flex;
      flex-direction: column;
      position: relative;
      isolation: isolate;
      transition: box-shadow 0.2s ease;

      &:hover {
        box-shadow: 0 8px 24px rgba($shop-black, 0.22);
      }

      .productTitleLink {
        color: inherit;
        text-decoration: none;
        cursor: pointer;

        &:hover {

          text-decoration: underline;

        }
      }

      .productImage {
        display: block;
        cursor: pointer;
      }

      .productImageContent {

        height: 100%;

      }
      .productImage:focus-visible, .productTitleLink:focus-visible {
        outline: 2px solid var(--v-primary-base);
        outline-offset: 2px;
      }

      &.highlight {
        overflow: clip;

        .productHighlight {
          position: absolute;
          inset: 0;
          container-type: size;
          pointer-events: none;
          z-index: 1;
        }

        .productHighlight::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          // Width + height is a safe fallback; the diagonal covers the card at every angle.
          // Two extra pixels avoid seams from fractional layout and rasterization rounding.
          width: calc(100cqw + 100cqh + 2px);
          height: calc(100cqw + 100cqh + 2px);
          width: calc(hypot(100cqw, 100cqh) + 2px);
          height: calc(hypot(100cqw, 100cqh) + 2px);
          background: conic-gradient(
            from 0deg,
            rgba($shop-highlight-gold, 0.6) 0deg,
            rgba($shop-highlight-orange, 0.6) 60deg,
            rgba($shop-highlight-pink, 0.6) 120deg,
            rgba($shop-highlight-purple, 0.6) 180deg,
            rgba($shop-highlight-violet, 0.6) 240deg,
            rgba($shop-highlight-brown, 0.6) 300deg,
            rgba($shop-highlight-gold, 0.6) 360deg
          );
          // Four seconds / 40 steps = one rotation update every 100 ms.
          animation: rotate-shimmer $highlight-shimmer-duration steps(40, end) infinite;
          pointer-events: none;
          z-index: 1;
          border-radius: 4px;
          opacity: 0.8;
        }

        &::after {
          content: '';
          position: absolute;
          top: $highlight-border-width;
          left: $highlight-border-width;
          right: $highlight-border-width;
          bottom: $highlight-border-width;
          background: $shop-white;
          border-radius: 2px;
          z-index: 2;
        }
      }

      .productImage,
      .productInfo,
      .v-card__actions {
        position: relative;
        z-index: 3;
      }

      .productImage {
        flex: none;
        height: 180px;
        margin: 0;
        border-top-left-radius: inherit;
        border-top-right-radius: inherit;
        background-color: $shop-image-surface;
      }

      .productInfo {
        // Keep the product text above the highlighted card's decorative cover.
        position: static;
        flex: 1;
        min-width: 0;
        padding: 12px 16px $highlight-border-width;
        display: flex;
        flex-direction: column;
      }

      .productBody {
        display: flex;
        flex: 1;
        flex-direction: column;
        min-width: 0;
      }

      .productHeader {
        padding: 0;
        margin-bottom: 8px;
        word-break: break-word;
        font-size: 1rem;
        font-weight: 700;
        line-height: 1.4;
      }

      .productContents {
        flex: 1;
        margin-bottom: 12px;
        font-size: 0.8125rem;
        line-height: 1.4;
        overflow-wrap: anywhere;

        ::v-deep p {
          margin-bottom: 0;
        }

        ::v-deep ul,
        ::v-deep ol {
          padding-left: 1.5em;
        }

        .bulletPoints {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          overflow: visible;
          gap: 5px 8px;
          margin: 0;
          padding: 0;
          list-style: none;

          @media (max-width: 767px) {
            grid-template-columns: repeat(auto-fit, minmax(min(100%, max(10rem, calc((100% - 8px) / 2))), 1fr));
          }

          @media (min-width: 768px) and (max-width: 959px) {
            grid-template-columns: repeat(auto-fit, minmax(min(100%, max(10rem, calc((100% - 16px) / 3))), 1fr));
          }

          & + .bulletPoints {
            margin-top: 10px;
          }

          li {
            display: flex;
            align-items: start;
            gap: 4px;
            min-width: 0;
            text-align: left;

            &:only-child {
              grid-column: 1 / -1;
            }
          }

          span {
            flex: 0 1 auto;
            min-width: 0;
            white-space: normal;
          }

          .bulletIcon {
            flex: 0 0 auto;
            width: 18px;
            height: 18px;
            font-size: 16px;
            line-height: 18px;
          }
        }

        .highlightedPoints {
          color: var(--v-accent-base);
          font-size: 0.875rem;
          font-weight: 400;

          .bulletIcon {
            width: 16px;
            height: 16px;
            margin-top: 2px;
            border-radius: 50%;
            display: grid;
            place-items: center;
            font-size: 10px;
            line-height: 16px;

            &::before {

              line-height: 1;

            }
          }
        }

        @media (max-width: 959px) {
          .summaryPoints {
            font-size: 0.75rem;
          }
        }
      }

      .actions {

        margin: auto 0 0;

      }
    }
  }
}

@media screen and (min-width: 960px) {
  .products {
    max-width: 1440px;
    margin-inline: auto;
  }

  .product-row .product-col .productCard .productImage {
    height: auto;
    aspect-ratio: 1;
  }

  // Keep only the decorative border visible on highlighted cards.
  .product-row .product-col .productCard.highlight .productImage {
    margin: $highlight-border-width $highlight-border-width 0;
  }
}

@media screen and (min-width: 1264px) {
  .product-row .product-col {
    flex-basis: 25%;
    max-width: 25%;
  }
}

@media screen and (max-width: 959px) {
  .product-row .product-col {
    flex-basis: 100%;
    max-width: 100%;

    .productCard {
      cursor: pointer;
      --product-image-width: clamp(96px, 30vw, 160px);
      display: flow-root;
      overflow: clip;
      padding: $mobile-card-padding;

      .productImage {
        float: left;
        width: var(--product-image-width);
        height: auto;
        aspect-ratio: 1;
        margin: 0 0 12px;
        align-self: start;
        border: 1px solid $shop-border;
        border-radius: 4px;
      }

      .productInfo {
        display: contents;
      }

      // Use the same normal-flow sticky boundary as the detail page, below the title.
      .productBody {
        display: block;
        padding-top: 12px;
        position: relative;
        z-index: 3;
        // Leave the highlight border exposed and room for the button shadow.
        clip-path: inset(-$mobile-card-padding ($highlight-border-width - $mobile-card-padding) ($highlight-border-width - $mobile-card-padding));
      }

      .productHeader {
        position: relative;
        z-index: 3;
        margin-top: -2px;
        margin-bottom: 0;
        margin-left: calc(var(--product-image-width) + 12px);
        display: flow-root;
      }

      .discountBadge {
        position: static;
        float: right;
        margin: 0 -3px 0 6px;
      }

      .productContents {
        margin-left: calc(var(--product-image-width) + 12px);
        margin-bottom: 0;
        // Narrow cards and enlarged text must wrap within the text column.
        .bulletPoints {
          overflow: visible;
          span {
            flex: 0 1 auto;
            min-width: 0;
            white-space: normal;
          }
        }
      }

      .actions {
        clear: both;
        position: sticky;
        bottom: calc(var(--basketDockHeight, 0px) + 5px);
        margin: 12px 0 0;
        transform: none;

        // Cover the rest of this card below the bar, including the gap above the dock.
        &::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          height: 100vh;
          background: $shop-white;
          pointer-events: none;
          z-index: -1;
        }
      }
    }
  }
}

.christmas {
  padding: 10px 10px 0;
  border: 3px solid $shop-seasonal-gold;
  margin-bottom: 10px;

  .row {
    margin-bottom: 0;
  }

  .v-col > * {
    position: relative;
    top: 50%;
    transform: translateY(-50%);
  }

  .iconFlex {
    flex: 0 1;

    @media screen and (max-width: 1400px) {
      margin: 0 auto;
    }

    .christmasImage {
      $size: 75px;
      height: $size;
      border: 4px solid rgba($shop-seasonal-red, 0.8);
      border-radius: $size;
      padding: 6px;
      position: relative;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  .textFlex {
    .text {
      font-size: 16px;
      margin-left: 20px;
      margin-bottom: 0;
      position: relative;
      top: 50%;
      transform: translateY(-50%);
    }
  }

}

.category-tabs {
  margin-bottom: 20px;
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;

  &::-webkit-scrollbar {

    display: none;

  }
  .category-chip {
    flex: 0 0 auto;
    white-space: nowrap;
    height: 40px;
    min-width: 90px;
    border-radius: 25px;
    margin: 4px 6px;
    padding: 0 16px;
    background-color: $shop-category-surface;
    color: rgba($shop-black, 0.54) !important;
    text-indent: 0;
    transition: all 0.3s;

    // Match the old tabs: let the shimmer blend over the label as well as the background.
    ::v-deep .v-btn__content {
      position: static;
    }
    &.v-tab--active {
      background-color: var(--v-primary-base) !important;
      color: $shop-white !important;

      .v-icon {
        color: $shop-white !important;
      }
    }

    &:not(.v-tab--active):hover {
      background-color: $shop-category-hover;
    }

    &.highlight-chip {
      position: relative !important;
      overflow: hidden !important;

      &::before {
        content: '' !important;
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        width: 100% !important;
        height: 100% !important;
        opacity: 1 !important;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba($shop-highlight-gold, 0.8) 25%,
          rgba($shop-highlight-pink, 0.8) 50%,
          rgba($shop-highlight-violet, 0.8) 75%,
          transparent 100%
        ) !important;
        // Forty steps over the two-second sweep, followed by the existing one-second pause.
        animation: shimmer 3s steps(40, end) infinite !important;
        pointer-events: none !important;
        mix-blend-mode: screen !important;
      }

      &.v-tab--active::before {
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba($shop-white, 0.4) 25%,
          rgba($shop-white, 0.6) 50%,
          rgba($shop-white, 0.4) 75%,
          transparent 100%
        ) !important;
      }
    }
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  66.67% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes rotate-shimmer {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.show-all-row {
  margin-top: 20px;
  margin-bottom: 30px;
}

</style>

<style lang="scss">
@use "../stylesheets/Variables.scss" as *;


.paymentPromotion {
  i {
    color: rgba($shop-category-green, 0.9) !important;
  }
}

</style>
